import React, { useState, useEffect, useMemo, useRef } from 'react';
import { auth, db } from '../../../firebase/config';
import { collection, onSnapshot, query, orderBy, where, getDocs, getDoc, doc, deleteDoc } from 'firebase/firestore';
import { useAppSelector } from '../../../store';
import Table, { Column } from '../../../components/Table';
import { Markdown } from '../../../components/Markdown';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import Toast from '../../../components/Toast';
import Icon from '../../../components/Icon';
import { 
    FiSearch, 
    FiCheck, 
    FiX, 
    FiEye, 
    FiClock, 
    FiVideo, 
    FiRadio, 
    FiRefreshCw, 
    FiClipboard, 
    FiSend,
    FiShield,
    FiFileText,
    FiCpu,
    FiTrash2,
    FiCalendar
} from 'react-icons/fi';
import { 
    fetchCompanyIdAndPsoInfo,
    updateRequestStatus,
    forwardRequestToHead,
    type Request 
} from '../../request_management/request_form/service/requestManagementService';
import { fetchManagedPsoIds } from '../../protocol/service/protocolService';
import { 
    updateTravellingApprovalStatus,
    type TravellingApprovalRequest 
} from '../../travelling_approval/service/travellingApprovalService';
import { 
    fetchBranches,
    type ChangeOfVendor,
    type Branch
} from '../../request_management/change_of_vendor/service/changeOfVendorService';
import { fetchCompaniesData, type Company } from '../../companies/service/companiesService';
import CCTVPsoRequest from '../../hardware_request/cctv/view/CCTVPsoRequest';
import BAMSPsoRequest from '../../hardware_request/bams/view/BAMSPsoRequest';
import ChangeOfVendors from '../../request_management/change_of_vendor/view/ChangeOfVendors';
import dayjs from 'dayjs';

const formatDateGlobal = (dateVal: any): string => {
    if (!dateVal) return '—';
    let d;
    if (typeof dateVal === 'object' && dateVal.seconds) {
        d = dayjs(dateVal.seconds * 1000);
    } else {
        d = dayjs(dateVal);
    }
    if (!d.isValid()) return '—';
    return d.format('MMMM D, YYYY');
};

interface PersistedCache {
    generalRequests: Request[];
    hardwareRequests: any[];
    travelRequests: TravellingApprovalRequest[];
    covRequests: ChangeOfVendor[];
    
    managedPsoIds: string[];
    branches: Branch[];
    companies: Company[];
    locationNameMap: Record<string, string>;
    
    psoNameByCreatorId: Record<string, string>;
    psoEmailById: Record<string, string>;
    
    lastCompanyId?: string;
    subscribedGroups: Record<string, boolean>;
    loadedGroups: Record<string, boolean>;
    unsubscribes: Record<string, () => void>;
    listeners: Set<() => void>;
}

const cache: PersistedCache = {
    generalRequests: [],
    hardwareRequests: [],
    travelRequests: [],
    covRequests: [],
    
    managedPsoIds: [],
    branches: [],
    companies: [],
    locationNameMap: {},
    
    psoNameByCreatorId: {},
    psoEmailById: {},
    
    subscribedGroups: {},
    loadedGroups: {},
    unsubscribes: {},
    listeners: new Set()
};

export default function RequestSummary() {
    const user = useAppSelector((s: any) => s.user.currentUser);
    const bank = useAppSelector((s: any) => s.user.bank);
    const pso = useAppSelector((s: any) => s.user.pso);

    const bankId = bank?.bankDocId || sessionStorage.getItem('bankDocId') || '';
    const psoDocId = sessionStorage.getItem('psoDocId') || '';
    const userRole = sessionStorage.getItem('role') || '';
    const isBankUser = userRole === 'bank';
    const designation = pso?.designation || '';

    const isRegional = !!pso && /regional/i.test(designation);
    const isHead = !!pso && /(^|\s)head(\s|$)/i.test(designation.replace(/_/g, ' ')) && !isRegional;

    const [activeTab, setActiveTab] = useState<'cctv' | 'bams' | 'guard' | 'cctv_hw' | 'bams_hw' | 'travel' | 'cov'>('cctv');
    
    // Data lists synced with global session cache
    const [allGeneralRequests, setAllGeneralRequests] = useState<Request[]>(cache.generalRequests);
    const [allTravelRequests, setAllTravelRequests] = useState<TravellingApprovalRequest[]>(cache.travelRequests);
    const [allChangeOfVendorRequests, setAllChangeOfVendorRequests] = useState<ChangeOfVendor[]>(cache.covRequests);
    const [allHardwareRequests, setAllHardwareRequests] = useState<any[]>(cache.hardwareRequests);
    const [managedPsoIds, setManagedPsoIds] = useState<string[]>(cache.managedPsoIds);
    const [branches, setBranches] = useState<Branch[]>(cache.branches);
    const [companies, setCompanies] = useState<Company[]>(cache.companies);
    const [locationNameMap, setLocationNameMap] = useState<Record<string, string>>(cache.locationNameMap);
    const [psoNameByCreatorId, setPsoNameByCreatorId] = useState<Record<string, string>>(cache.psoNameByCreatorId);
    const [psoEmailById, setPsoEmailById] = useState<Record<string, string>>(cache.psoEmailById);
    const [companyId, setCompanyId] = useState<string>('');

    const [loadingTabs, setLoadingTabs] = useState<Record<string, boolean>>({
        cctv: !cache.loadedGroups['general'],
        bams: !cache.loadedGroups['general'],
        guard: !cache.loadedGroups['general'],
        cctv_hw: !cache.loadedGroups['hardware'],
        bams_hw: !cache.loadedGroups['hardware'],
        travel: !cache.loadedGroups['travel'],
        cov: !cache.loadedGroups['cov']
    });
    const [loadingStaticData, setLoadingStaticData] = useState(cache.branches.length === 0);
    const [loadingManagedPsoIds, setLoadingManagedPsoIds] = useState(isRegional && cache.managedPsoIds.length === 0);

    const loading = loadingTabs[activeTab] || loadingStaticData || (isRegional && loadingManagedPsoIds);

    // Keep component local states in sync with cache updates
    useEffect(() => {
        const handleCacheUpdate = () => {
            setAllGeneralRequests([...cache.generalRequests]);
            setAllHardwareRequests([...cache.hardwareRequests]);
            setAllTravelRequests([...cache.travelRequests]);
            setAllChangeOfVendorRequests([...cache.covRequests]);
            setManagedPsoIds([...cache.managedPsoIds]);
            setBranches([...cache.branches]);
            setCompanies([...cache.companies]);
            setLocationNameMap({ ...cache.locationNameMap });
            setPsoNameByCreatorId({ ...cache.psoNameByCreatorId });
            setPsoEmailById({ ...cache.psoEmailById });
            
            setLoadingTabs({
                cctv: !cache.loadedGroups['general'],
                bams: !cache.loadedGroups['general'],
                guard: !cache.loadedGroups['general'],
                cctv_hw: !cache.loadedGroups['hardware'],
                bams_hw: !cache.loadedGroups['hardware'],
                travel: !cache.loadedGroups['travel'],
                cov: !cache.loadedGroups['cov']
            });
            setLoadingStaticData(cache.branches.length === 0);
            setLoadingManagedPsoIds(isRegional && cache.managedPsoIds.length === 0);
        };

        cache.listeners.add(handleCacheUpdate);
        return () => {
            cache.listeners.delete(handleCacheUpdate);
        };
    }, [isRegional]);

    const resolvePsoNamesHelper = async (ids: string[]) => {
        if (!companyId) return;
        const unresolvedIds = ids.filter(id => !cache.psoNameByCreatorId[id]);
        if (unresolvedIds.length === 0) return;

        const batchName: Record<string, string> = {};
        const batchEmail: Record<string, string> = {};

        for (const id of unresolvedIds) {
            try {
                const { doc: fsDoc, getDoc, collection, query, where, getDocs } = await import('firebase/firestore');
                let s = await getDoc(fsDoc(db, 'banks', companyId, 'pos', id));
                let foundDoc = s.exists() ? s : null;
                if (!foundDoc) {
                    const q = query(collection(db, 'banks', companyId, 'pos'), where('authUid', '==', id));
                    const snap = await getDocs(q);
                    if (!snap.empty) {
                        foundDoc = snap.docs[0];
                    }
                }
                if (foundDoc) {
                    const data = foundDoc.data();
                    batchName[id] = String((data as { name?: string }).name || id);
                    batchEmail[id] = String((data as { email?: string }).email || 'N/A');
                } else {
                    batchName[id] = id;
                    batchEmail[id] = 'N/A';
                }
            } catch (err) {
                console.error('Error fetching PSO details helper:', err);
                batchName[id] = id;
                batchEmail[id] = 'N/A';
            }
        }

        cache.psoNameByCreatorId = { ...cache.psoNameByCreatorId, ...batchName };
        cache.psoEmailById = { ...cache.psoEmailById, ...batchEmail };
        cache.listeners.forEach(fn => fn());
    };

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');

    // Details Modals
    const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
    const [selectedTravelRequest, setSelectedTravelRequest] = useState<TravellingApprovalRequest | null>(null);
    const [selectedHwRequest, setSelectedHwRequest] = useState<any | null>(null);
    
    // Action Modals
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectingItem, setRejectingItem] = useState<{ id: string; type: 'general' | 'travel' | 'hardware' } | null>(null);
    
    // Travelling Approval Claim Confirm Modal
    const [showTravelApproveModal, setShowTravelApproveModal] = useState(false);
    const [confirmAmount, setConfirmAmount] = useState('');
    const [approverRemarks, setApproverRemarks] = useState('');
    const [approvingTravelId, setApprovingTravelId] = useState<string | null>(null);

    // Toast
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' }>({
        show: false,
        message: '',
        type: 'success'
    });

    const triggerToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
        setToast({ show: true, message, type });
    };

    // Load managed PSOs once on mount/auth load
    useEffect(() => {
        async function loadManagedIds() {
            if (!user?.uid) return;
            if (cache.managedPsoIds.length > 0) {
                return;
            }
            try {
                const info = await fetchCompanyIdAndPsoInfo(user.uid);
                const resolvedBankId = bankId || info?.companyId || '';
                const resolvedPsoDocId = psoDocId || info?.posDocId || '';
                
                if (resolvedBankId && resolvedPsoDocId) {
                    const ids = await fetchManagedPsoIds(resolvedBankId, resolvedPsoDocId);
                    cache.managedPsoIds = ids;
                    cache.listeners.forEach(fn => fn());
                }
            } catch (err) {
                console.error('Error fetching managed PSOs for summary:', err);
            } finally {
                setLoadingManagedPsoIds(false);
            }
        }
        loadManagedIds();
    }, [user, bankId, psoDocId]);



    // Resolve Company ID and load static data (branches, companies, locations)
    useEffect(() => {
        if (!user?.uid) return;
        
        let cancelled = false;
        async function init() {
            let resolvedBankId = bankId;
            if (!resolvedBankId) {
                const info = await fetchCompanyIdAndPsoInfo(user.uid);
                if (info) {
                    resolvedBankId = info.companyId;
                }
            }
            if (!resolvedBankId) {
                const { doc: fsDoc, getDoc } = await import('firebase/firestore');
                const directBank = await getDoc(fsDoc(db, "banks", user.uid));
                if (directBank.exists()) {
                    resolvedBankId = user.uid;
                }
            }
            if (!resolvedBankId) {
                return;
            }
            
            if (cancelled) return;
            setCompanyId(resolvedBankId);

            if (cache.branches.length > 0 && cache.companies.length > 0 && Object.keys(cache.locationNameMap).length > 0) {
                setLoadingStaticData(false);
                return;
            }

            try {
                const branchesList = await fetchBranches(resolvedBankId);
                if (cancelled) return;
                cache.branches = branchesList;

                const companiesList = await fetchCompaniesData(user.uid, userRole === 'bank');
                if (cancelled) return;
                cache.companies = companiesList;
                
                const { collection, getDocs } = await import('firebase/firestore');
                const locationsSnap = await getDocs(collection(db, 'locations'));
                if (cancelled) return;
                const locMap: Record<string, string> = {};
                locationsSnap.forEach(doc => {
                    locMap[doc.id] = doc.data().name || '';
                });
                cache.locationNameMap = locMap;
                
                cache.listeners.forEach(fn => fn());
            } catch (err) {
                console.error('Error loading static data:', err);
            } finally {
                if (!cancelled) {
                    setLoadingStaticData(false);
                }
            }
        }
        
        init();
        return () => {
            cancelled = true;
        };
    }, [user?.uid, bankId, userRole]);

    // Setup Parallel Real-Time Firestore streams and load static lists
    useEffect(() => {
        if (!companyId) return;

        // If companyId has changed, unsubscribe from old streams and reset cache
        if (cache.lastCompanyId && cache.lastCompanyId !== companyId) {
            Object.values(cache.unsubscribes).forEach(unsub => unsub());
            cache.unsubscribes = {};
            cache.subscribedGroups = {};
            cache.loadedGroups = {};
            cache.generalRequests = [];
            cache.hardwareRequests = [];
            cache.travelRequests = [];
            cache.covRequests = [];
            cache.managedPsoIds = [];
            cache.branches = [];
            cache.companies = [];
            cache.locationNameMap = {};
        }
        cache.lastCompanyId = companyId;

        // 1. General Requests Stream
        if (!cache.subscribedGroups['general']) {
            cache.subscribedGroups['general'] = true;
            const qGeneral = query(
                collection(db, 'banks', companyId, 'requests'),
                orderBy('createdAt', 'desc')
            );
            const unsubscribeGeneral = onSnapshot(qGeneral, async (snapshot) => {
                const list = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Request[];

                const ids = Array.from(
                    new Set(
                        list.flatMap((r) => [r.createdBy, r.forwardedBy, r.updatedBy]).filter(Boolean)
                    )
                ) as string[];
                if (ids.length > 0) {
                    await resolvePsoNamesHelper(ids);
                }

                cache.generalRequests = list;
                cache.loadedGroups['general'] = true;
                cache.listeners.forEach(fn => fn());
            }, (error) => {
                console.error('General requests stream error:', error);
                cache.loadedGroups['general'] = true;
                cache.listeners.forEach(fn => fn());
            });
            cache.unsubscribes['general'] = unsubscribeGeneral;
        }

        // 2. Hardware Requests Stream
        if (!cache.subscribedGroups['hardware']) {
            cache.subscribedGroups['hardware'] = true;
            const qHardware = query(
                collection(db, 'banks', companyId, 'hardware_requests'),
                orderBy('createdAt', 'desc')
            );
            const unsubscribeHardware = onSnapshot(qHardware, async (snapshot) => {
                const list = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                const ids = Array.from(
                    new Set(
                        list.flatMap((r: any) => [r.createdBy, r.forwardedBy, r.updatedBy]).filter(Boolean)
                    )
                ) as string[];
                if (ids.length > 0) {
                    await resolvePsoNamesHelper(ids);
                }

                cache.hardwareRequests = list;
                cache.loadedGroups['hardware'] = true;
                cache.listeners.forEach(fn => fn());
            }, (error) => {
                console.error('Hardware requests stream error:', error);
                cache.loadedGroups['hardware'] = true;
                cache.listeners.forEach(fn => fn());
            });
            cache.unsubscribes['hardware'] = unsubscribeHardware;
        }

        // 3. Travelling Approvals Stream
        if (!cache.subscribedGroups['travel']) {
            cache.subscribedGroups['travel'] = true;
            const qTravel = query(
                collection(db, 'banks', companyId, 'travelling_approvals'),
                orderBy('createdAt', 'desc')
            );
            const unsubscribeTravel = onSnapshot(qTravel, async (snapshot) => {
                const list = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as TravellingApprovalRequest[];

                const ids = Array.from(
                    new Set(
                        list.flatMap((r: any) => [r.createdBy, r.forwardedBy, r.updatedBy]).filter(Boolean)
                    )
                ) as string[];
                if (ids.length > 0) {
                    await resolvePsoNamesHelper(ids);
                }

                cache.travelRequests = list;
                cache.loadedGroups['travel'] = true;
                cache.listeners.forEach(fn => fn());
            }, (error) => {
                console.error('Travel approvals stream error:', error);
                cache.loadedGroups['travel'] = true;
                cache.listeners.forEach(fn => fn());
            });
            cache.unsubscribes['travel'] = unsubscribeTravel;
        }

        // 4. Change of Vendor Stream
        if (!cache.subscribedGroups['cov']) {
            cache.subscribedGroups['cov'] = true;
            const qChangeOfVendor = query(
                collection(db, 'banks', companyId, 'changeOfVendor'),
                orderBy('createdAt', 'desc')
            );
            const unsubscribeChangeOfVendor = onSnapshot(qChangeOfVendor, async (snapshot) => {
                const list = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as ChangeOfVendor[];

                const ids = Array.from(
                    new Set(
                        list.flatMap((r: any) => [r.createdBy, r.forwardedBy, r.updatedBy, r.psoId]).filter(Boolean)
                    )
                ) as string[];
                if (ids.length > 0) {
                    await resolvePsoNamesHelper(ids);
                }

                cache.covRequests = list;
                cache.loadedGroups['cov'] = true;
                cache.listeners.forEach(fn => fn());
            }, (error) => {
                console.error('Change of vendor requests stream error:', error);
                cache.loadedGroups['cov'] = true;
                cache.listeners.forEach(fn => fn());
            });
            cache.unsubscribes['cov'] = unsubscribeChangeOfVendor;
        }
    }, [companyId]);



    // Filtering logic for CCTV General requests
    const cctvRequests = useMemo(() => {
        let list = allGeneralRequests.filter(r => r.requestType?.toLowerCase() === 'cctv');

        // Filter based on designation permissions and status (Pending only)
        if (isRegional) {
            list = list.filter(r => managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending');
        } else if (isHead) {
            list = list.filter(r => r.status === 'forwarded_to_head');
        } else {
            list = list.filter(r => r.status?.toLowerCase() === 'pending');
        }

        // Apply Search Term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.requestId || '').toLowerCase().includes(term) ||
                (r.branchCode || '').toLowerCase().includes(term) ||
                (r.branchName || '').toLowerCase().includes(term) ||
                (r.requesterName || '').toLowerCase().includes(term)
            );
        }

        return list;
    }, [allGeneralRequests, isRegional, isHead, managedPsoIds, searchTerm]);

    // Filtering logic for BAMS General requests
    const bamsRequests = useMemo(() => {
        let list = allGeneralRequests.filter(r => r.requestType?.toLowerCase() === 'bams');

        // Filter based on designation permissions and status (Pending only)
        if (isRegional) {
            list = list.filter(r => managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending');
        } else if (isHead) {
            list = list.filter(r => r.status === 'forwarded_to_head');
        } else {
            list = list.filter(r => r.status?.toLowerCase() === 'pending');
        }

        // Apply Search Term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.requestId || '').toLowerCase().includes(term) ||
                (r.branchCode || '').toLowerCase().includes(term) ||
                (r.branchName || '').toLowerCase().includes(term) ||
                (r.requesterName || '').toLowerCase().includes(term)
            );
        }

        return list;
    }, [allGeneralRequests, isRegional, isHead, managedPsoIds, searchTerm]);

    // Filtering logic for Guard requests
    const guardRequests = useMemo(() => {
        let list = allGeneralRequests.filter(r => r.requestType?.toLowerCase() === 'guard');

        // Filter based on designation permissions and status (Pending only)
        if (isRegional) {
            list = list.filter(r => managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending');
        } else if (isHead) {
            list = list.filter(r => r.status === 'forwarded_to_head');
        } else {
            list = list.filter(r => r.status?.toLowerCase() === 'pending');
        }

        // Apply Search Term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.requestId || '').toLowerCase().includes(term) ||
                (r.branchCode || '').toLowerCase().includes(term) ||
                (r.branchName || '').toLowerCase().includes(term) ||
                (r.requesterName || '').toLowerCase().includes(term)
            );
        }

        return list;
    }, [allGeneralRequests, isRegional, isHead, managedPsoIds, searchTerm]);

    // Filtering logic for CCTV Hardware requests
    const cctvHardwareRequests = useMemo(() => {
        let list = allHardwareRequests.filter(r => r.requestType === 'CCTV');

        // Filter based on designation permissions and status
        if (isRegional) {
            list = list.filter(r => {
                if (r.docType === 'pso_request') {
                    return managedPsoIds.includes(r.psoId || r.createdBy || '') && r.status === 'forwarded_to_regional';
                } else {
                    return managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending';
                }
            });
        } else if (isHead) {
            list = list.filter(r => {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            });
        } else {
            list = list.filter(r => {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded_to_regional' || r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            });
        }

        // Apply Search Term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.requestId || '').toLowerCase().includes(term) ||
                (r.poNumber || '').toLowerCase().includes(term) ||
                (r.branchCode || '').toLowerCase().includes(term) ||
                (r.branchName || '').toLowerCase().includes(term) ||
                (r.vendor || '').toLowerCase().includes(term)
            );
        }

        return list;
    }, [allHardwareRequests, isRegional, isHead, managedPsoIds, searchTerm]);

    // Filtering logic for BAMS Hardware requests
    const bamsHardwareRequests = useMemo(() => {
        let list = allHardwareRequests.filter(r => r.requestType === 'BAMS');

        // Filter based on designation permissions and status
        if (isRegional) {
            list = list.filter(r => {
                if (r.docType === 'pso_request') {
                    return managedPsoIds.includes(r.psoId || r.createdBy || '') && r.status === 'forwarded_to_regional';
                } else {
                    return managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending';
                }
            });
        } else if (isHead) {
            list = list.filter(r => {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            });
        } else {
            list = list.filter(r => {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded_to_regional' || r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            });
        }

        // Apply Search Term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.requestId || '').toLowerCase().includes(term) ||
                (r.poNumber || '').toLowerCase().includes(term) ||
                (r.branchCode || '').toLowerCase().includes(term) ||
                (r.branchName || '').toLowerCase().includes(term) ||
                (r.vendor || '').toLowerCase().includes(term)
            );
        }

        return list;
    }, [allHardwareRequests, isRegional, isHead, managedPsoIds, searchTerm]);

    // Filtering logic for Travelling Approvals
    const filteredTravelRequests = useMemo(() => {
        let list = allTravelRequests;

        // Filter based on designation permissions and status (Pending/Forwarded only)
        if (isRegional) {
            list = list.filter(r => managedPsoIds.includes(r.psoId || '') && r.status === 'pending');
        } else if (isHead) {
            list = list.filter(r => r.status === 'forwarded');
        } else {
            list = list.filter(r => r.status === 'pending');
        }

        // Apply Search Term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.requestId || '').toLowerCase().includes(term) ||
                (r.psoName || '').toLowerCase().includes(term) ||
                (r.psoEmail || '').toLowerCase().includes(term) ||
                (r.branchNames || []).some(name => name.toLowerCase().includes(term))
            );
        }

        return list;
    }, [allTravelRequests, isRegional, isHead, managedPsoIds, searchTerm]);

    // Enrich Change of Vendor requests
    const enrichedChangeOfVendorRequests = useMemo(() => {
        return allChangeOfVendorRequests.map(r => {
            const branch = branches.find(b => b.id === r.branchId);
            const company = companies.find(c => c.id === r.companiesId);
            return {
                ...r,
                branch: branch || null,
                company: company || null
            };
        });
    }, [allChangeOfVendorRequests, branches, companies]);

    // Filtering logic for Change of Vendor requests
    const filteredChangeOfVendorRequests = useMemo(() => {
        let list = enrichedChangeOfVendorRequests;

        // Filter based on designation permissions and status (Pending only)
        if (isRegional) {
            list = list.filter(r => managedPsoIds.includes(r.psoId || r.createdBy || ''));
        }

        if (isHead) {
            list = list.filter(r => r.status?.toLowerCase() === 'forwarded to head');
        } else {
            list = list.filter(r => r.status?.toLowerCase() === 'pending');
        }

        // Apply Search Term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.requestId || '').toLowerCase().includes(term) ||
                (r.branch?.branchCode || '').toLowerCase().includes(term) ||
                (r.branch?.address || '').toLowerCase().includes(term) ||
                (r.company?.companyName || '').toLowerCase().includes(term)
            );
        }

        return list;
    }, [enrichedChangeOfVendorRequests, isRegional, isHead, managedPsoIds, searchTerm]);

    // KPI Counts calculation (Pending only)
    const counts = useMemo(() => {
        const pendingCctv = allGeneralRequests.filter(r => {
            const isMatch = r.requestType?.toLowerCase() === 'cctv';
            if (!isMatch) return false;
            if (isRegional) {
                return managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending';
            }
            if (isHead) {
                return r.status === 'forwarded_to_head';
            }
            return r.status?.toLowerCase() === 'pending';
        }).length;

        const pendingBams = allGeneralRequests.filter(r => {
            const isMatch = r.requestType?.toLowerCase() === 'bams';
            if (!isMatch) return false;
            if (isRegional) {
                return managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending';
            }
            if (isHead) {
                return r.status === 'forwarded_to_head';
            }
            return r.status?.toLowerCase() === 'pending';
        }).length;

        const pendingGuard = allGeneralRequests.filter(r => {
            const isMatch = r.requestType?.toLowerCase() === 'guard';
            if (!isMatch) return false;
            if (isRegional) {
                return managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending';
            }
            if (isHead) {
                return r.status === 'forwarded_to_head';
            }
            return r.status?.toLowerCase() === 'pending';
        }).length;

        const pendingCctvHw = allHardwareRequests.filter(r => {
            const isMatch = r.requestType === 'CCTV';
            if (!isMatch) return false;
            if (isRegional) {
                if (r.docType === 'pso_request') {
                    return managedPsoIds.includes(r.psoId || r.createdBy || '') && r.status === 'forwarded_to_regional';
                } else {
                    return managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending';
                }
            } else if (isHead) {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            } else {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded_to_regional' || r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            }
        }).length;

        const pendingBamsHw = allHardwareRequests.filter(r => {
            const isMatch = r.requestType === 'BAMS';
            if (!isMatch) return false;
            if (isRegional) {
                if (r.docType === 'pso_request') {
                    return managedPsoIds.includes(r.psoId || r.createdBy || '') && r.status === 'forwarded_to_regional';
                } else {
                    return managedPsoIds.includes(r.createdBy || '') && r.status?.toLowerCase() === 'pending';
                }
            } else if (isHead) {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            } else {
                if (r.docType === 'pso_request') {
                    return r.status === 'forwarded_to_regional' || r.status === 'forwarded';
                } else {
                    return r.status?.toLowerCase() === 'pending';
                }
            }
        }).length;

        const pendingTravel = allTravelRequests.filter(r => {
            if (isRegional) {
                return managedPsoIds.includes(r.psoId || '') && r.status === 'pending';
            }
            if (isHead) {
                return r.status === 'forwarded';
            }
            return r.status === 'pending';
        }).length;

        const pendingCov = allChangeOfVendorRequests.filter(r => {
            const statusLower = r.status?.toLowerCase() || '';
            if (isRegional) {
                return managedPsoIds.includes(r.psoId || r.createdBy || '') && statusLower === 'pending';
            }
            if (isHead) {
                return statusLower === 'forwarded to head';
            }
            return statusLower === 'pending';
        }).length;

        return {
            cctv: pendingCctv,
            bams: pendingBams,
            guard: pendingGuard,
            cctvHw: pendingCctvHw,
            bamsHw: pendingBamsHw,
            travel: pendingTravel,
            cov: pendingCov,
            pending: pendingCctv + pendingBams + pendingGuard + pendingCctvHw + pendingBamsHw + pendingTravel + pendingCov
        };
    }, [allGeneralRequests, allTravelRequests, allChangeOfVendorRequests, allHardwareRequests, isRegional, isHead, managedPsoIds]);

    // Approve Handler for CCTV, BAMS, Guard
    const handleApproveGeneral = async (request: Request) => {
        const info = await fetchCompanyIdAndPsoInfo(user?.uid || '');
        const resolvedBankId = bankId || info?.companyId || '';
        if (!resolvedBankId || !user?.uid) return;
        
        if (!window.confirm('Are you sure you want to approve this request?')) return;

        try {
            await updateRequestStatus(resolvedBankId, request.id, 'approved', user.uid);
            triggerToast('Request approved successfully!', 'success');
        } catch (err) {
            console.error('Approve failed:', err);
            triggerToast('Failed to approve request', 'error');
        }
    };

    // Forward to Head Handler for CCTV, BAMS, Guard
    const handleForwardGeneral = async (request: Request) => {
        const info = await fetchCompanyIdAndPsoInfo(user?.uid || '');
        const resolvedBankId = bankId || info?.companyId || '';
        if (!resolvedBankId || !user?.uid) return;

        if (!window.confirm('Are you sure you want to forward this request to Head?')) return;

        try {
            await forwardRequestToHead(resolvedBankId, request.id, user.uid);
            triggerToast('Request forwarded to Head successfully!', 'success');
        } catch (err) {
            console.error('Forward failed:', err);
            triggerToast('Failed to forward request', 'error');
        }
    };

    // Approve Handler for Hardware Requests (CCTV, BAMS)
    const handleApproveHw = async (request: any) => {
        const info = await fetchCompanyIdAndPsoInfo(user?.uid || '');
        const resolvedBankId = bankId || info?.companyId || '';
        if (!resolvedBankId || !user?.uid) return;

        if (!window.confirm('Are you sure you want to approve this hardware request?')) return;

        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', resolvedBankId, 'hardware_requests', request.id), {
                status: 'approved',
                updatedAt: serverTimestamp(),
                updatedBy: user.uid,
            });
            triggerToast('Hardware request approved successfully!', 'success');
        } catch (err) {
            console.error('Approve failed:', err);
            triggerToast('Failed to approve hardware request', 'error');
        }
    };

    // Delete Handler for Hardware Requests
    const handleDeleteHw = async (request: any) => {
        const info = await fetchCompanyIdAndPsoInfo(user?.uid || '');
        const resolvedBankId = bankId || info?.companyId || '';
        if (!resolvedBankId) return;

        if (!window.confirm('Are you sure you want to permanently delete this hardware request?')) return;

        try {
            await deleteDoc(doc(db, 'banks', resolvedBankId, 'hardware_requests', request.id));
            triggerToast('Hardware request deleted successfully!', 'success');
            setSelectedHwRequest(null);
        } catch (err) {
            console.error('Delete failed:', err);
            triggerToast('Failed to delete hardware request', 'error');
        }
    };



    // Forward Travel Request to Head
    const handleForwardTravel = async (travelId: string) => {
        if (!bankId) return;
        if (!window.confirm('Are you sure you want to forward this travel claim to Head?')) return;

        try {
            await updateTravellingApprovalStatus(bankId, travelId, 'forwarded');
            triggerToast('Travel claim forwarded to Head successfully!', 'success');
            setSelectedTravelRequest(null);
        } catch (err) {
            console.error('Forward travel failed:', err);
            triggerToast('Failed to forward travel claim', 'error');
        }
    };

    // Travel approval claim triggers
    const triggerTravelApprove = (travelId: string, currentAmount: number) => {
        setApprovingTravelId(travelId);
        setConfirmAmount(String(currentAmount));
        setApproverRemarks('');
        setShowTravelApproveModal(true);
    };

    // Confirm Travel Approval
    const handleConfirmTravelApprove = async () => {
        if (!approvingTravelId || !confirmAmount.trim() || !bankId) return;

        try {
            await updateTravellingApprovalStatus(
                bankId,
                approvingTravelId,
                'approved',
                approverRemarks.trim(),
                Number(confirmAmount)
            );
            triggerToast('Travel claim approved successfully!', 'success');
            setShowTravelApproveModal(false);
            setSelectedTravelRequest(null);
        } catch (err) {
            console.error('Approve travel claim failed:', err);
            triggerToast('Failed to approve travel claim', 'error');
        }
    };

    // Reject Button Trigger
    const triggerReject = (id: string, type: 'general' | 'travel' | 'hardware') => {
        setRejectingItem({ id, type });
        setRejectReason('');
        setShowRejectModal(true);
    };

    // Confirm Rejection
    const handleConfirmReject = async () => {
        if (!rejectingItem || !rejectReason.trim()) return;
        const info = await fetchCompanyIdAndPsoInfo(user?.uid || '');
        const resolvedBankId = bankId || info?.companyId || '';
        if (!resolvedBankId || !user?.uid) return;

        try {
            if (rejectingItem.type === 'general') {
                await updateRequestStatus(resolvedBankId, rejectingItem.id, 'rejected', user.uid, {
                    rejectedReason: rejectReason.trim(),
                    rejectedByName: pso?.name || 'Regional Manager'
                });
                setSelectedRequest(null);
            } else if (rejectingItem.type === 'travel') {
                await updateTravellingApprovalStatus(
                    resolvedBankId,
                    rejectingItem.id,
                    'rejected',
                    rejectReason.trim()
                );
                setSelectedTravelRequest(null);
            } else if (rejectingItem.type === 'hardware') {
                const { updateDoc, serverTimestamp } = await import('firebase/firestore');
                await updateDoc(doc(db, 'banks', resolvedBankId, 'hardware_requests', rejectingItem.id), {
                    status: 'rejected',
                    rejectReason: rejectReason.trim(),
                    rejectedAt: serverTimestamp(),
                    rejectedBy: user.uid,
                    updatedAt: serverTimestamp(),
                    updatedBy: user.uid,
                });
                setSelectedHwRequest(null);
            }
            triggerToast('Request rejected successfully!', 'success');
            setShowRejectModal(false);
        } catch (err) {
            console.error('Rejection failed:', err);
            triggerToast('Failed to reject request', 'error');
        }
    };



    // Render helpers for columns
    const renderBranchColumn = (row: any) => {
        const branch = branches.find(b => b.id === row.branchId || b.branchCode === row.branchCode);
        const locationName = branch?.locationName || (branch?.locationId ? locationNameMap[branch.locationId] : '') || '';
        return (
            <div className="flex flex-col">
                <span className="font-bold text-slate-900 dark:text-white">{row.branchCode || '—'}</span>
                <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">{row.branchName || '—'}</span>
                {locationName && (
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded self-start mt-1">
                        {locationName}
                    </span>
                )}
            </div>
        );
    };

    const renderPsoColumn = (row: any) => {
        const id = row.createdBy || '';
        const psoName = id ? psoNameByCreatorId[id] : '';
        const forwardedByName = row.forwardedBy ? psoNameByCreatorId[row.forwardedBy] : '';
        return (
            <div className="flex flex-col">
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {psoName || row.requesterName || id || '—'}
                </span>
                {isHead && forwardedByName && (
                    <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">
                        Forwarded by: {forwardedByName}
                    </span>
                )}
            </div>
        );
    };

    const getFormattedPurpose = (request: any): string => {
        const p = String(request.purposeOfFootage || request.purpose || '').trim();
        const other = String(request.purposeOther || request.purpose_other || '').trim();
        if (p.toLowerCase() === 'other' && other) {
            return other;
        }
        if (p.toLowerCase() === 'police pressure' && other) {
            return `${p}: ${other}`;
        }
        return p || 'N/A';
    };

    // Columns Definition: CCTV Requests
    const cctvColumns: Column<Request>[] = [
        {
            header: 'Request ID',
            render: (row: Request) => <span className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200">{row.requestId || row.id.slice(-6).toUpperCase()}</span>
        },
        {
            header: 'Branch Code / Address',
            width: '28%',
            render: renderBranchColumn
        },
        {
            header: 'PSO',
            render: renderPsoColumn
        },
        {
            header: 'Footage Range',
            render: (row: Request) => {
                const ranges = row.cctvDateRanges;
                if (ranges && ranges.length > 0) {
                    const startDates = ranges.map(r => r.dateFrom).filter(Boolean);
                    const endDates = ranges.map(r => r.dateTo).filter(Boolean);
                    if (startDates.length > 0 && endDates.length > 0) {
                        const minDate = startDates.reduce((min, d) => d < min ? d : min, startDates[0]);
                        const maxDate = endDates.reduce((max, d) => d > max ? d : max, endDates[0]);
                        return (
                            <div className="flex flex-col text-xs font-medium">
                                <span>{formatDateGlobal(minDate)} &rarr; {formatDateGlobal(maxDate)}</span>
                                <span className="text-slate-450 dark:text-slate-500 text-[10px] font-semibold">({ranges.length} ranges)</span>
                            </div>
                        );
                    }
                }
                return (
                    <div className="flex flex-col text-xs font-medium">
                        <span>From: {formatDateGlobal(row.cctvDateFrom)} ({row.timeFrom || '—'})</span>
                        <span className="text-slate-400 dark:text-slate-500">To: {formatDateGlobal(row.cctvDateTo)} ({row.timeTo || '—'})</span>
                    </div>
                );
            }
        },
        {
            header: 'No. of Days',
            render: (row: Request) => {
                const inclusiveDaysBetween = (fromStr: string, toStr: string): number => {
                    if (!fromStr || !toStr) return 0;
                    const fromUtc = Date.UTC(
                        Number(fromStr.slice(0, 4)),
                        Number(fromStr.slice(5, 7)) - 1,
                        Number(fromStr.slice(8, 10))
                    );
                    const toUtc = Date.UTC(
                        Number(toStr.slice(0, 4)),
                        Number(toStr.slice(5, 7)) - 1,
                        Number(toStr.slice(8, 10))
                    );
                    const diffDays = Math.floor((toUtc - fromUtc) / (1000 * 60 * 60 * 24));
                    return diffDays + 1;
                };

                const ranges = row.cctvDateRanges;
                if (ranges && ranges.length > 0) {
                    let totalDays = 0;
                    ranges.forEach((range: any) => {
                        totalDays += inclusiveDaysBetween(range.dateFrom, range.dateTo);
                    });
                    return <span className="font-semibold text-slate-800 dark:text-slate-200">{totalDays}</span>;
                }
                const dateFrom = row.cctvDateFrom || '';
                const dateTo = row.cctvDateTo || '';
                const days = inclusiveDaysBetween(dateFrom, dateTo);
                return <span className="font-semibold text-slate-800 dark:text-slate-200">{days || '-'}</span>;
            }
        },
        {
            header: 'Status',
            render: (row: Request) => (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700 dark:bg-amber-950/20">
                    PENDING
                </span>
            )
        },
        {
            header: 'Actions',
            align: 'right',
            width: '18%',
            render: (row: Request) => (
                <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={() => setSelectedRequest(row)}
                        className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => handleApproveGeneral(row)}
                        className="rounded-lg p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/20"
                        title="Approve"
                    >
                        <Icon icon={FiCheck} className="h-4 w-4" />
                    </button>
                    {isRegional && (
                        <button
                            type="button"
                            onClick={() => handleForwardGeneral(row)}
                            className="rounded-lg p-1.5 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/20"
                            title="Forward to Head"
                        >
                            <Icon icon={FiSend} className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => triggerReject(row.id, 'general')}
                        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                        title="Reject"
                    >
                        <Icon icon={FiX} className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    const bamsColumns: Column<Request>[] = [
        {
            header: 'Request ID',
            render: (row: Request) => <span className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200">{row.requestId || row.id.slice(-6).toUpperCase()}</span>
        },
        {
            header: 'Branch Code / Address',
            width: '28%',
            render: renderBranchColumn
        },
        {
            header: 'PSO',
            render: renderPsoColumn
        },
        {
            header: 'Hardware Required',
            render: (row: Request) => <span className="font-medium text-slate-800 dark:text-slate-200 text-sm">{row.hardwareName || 'BAMS Controller'}</span>
        },
        {
            header: 'Required Dates',
            render: (row: Request) => (
                <div className="flex flex-col text-xs font-medium">
                    <span>From: {formatDateGlobal(row.bamsDateFrom)}</span>
                    <span className="text-slate-400 dark:text-slate-500">To: {formatDateGlobal(row.bamsDateTo)}</span>
                </div>
            )
        },
        {
            header: 'No. of Days',
            render: (row: Request) => {
                const from = row.bamsDateFrom || row.cctvDateFrom || '';
                const to = row.bamsDateTo || row.cctvDateTo || '';
                if (!from || !to) return <span className="text-slate-400">-</span>;
                const fromUtc = Date.UTC(Number(from.slice(0,4)), Number(from.slice(5,7))-1, Number(from.slice(8,10)));
                const toUtc = Date.UTC(Number(to.slice(0,4)), Number(to.slice(5,7))-1, Number(to.slice(8,10)));
                const days = Math.floor((toUtc - fromUtc) / (1000 * 60 * 60 * 24)) + 1;
                return <span className="font-semibold text-slate-800 dark:text-slate-200">{days}</span>;
            }
        },
        {
            header: 'Status',
            render: (row: Request) => (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700 dark:bg-amber-950/20">
                    PENDING
                </span>
            )
        },
        {
            header: 'Actions',
            align: 'right',
            width: '18%',
            render: (row: Request) => (
                <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={() => setSelectedRequest(row)}
                        className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => handleApproveGeneral(row)}
                        className="rounded-lg p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/20"
                        title="Approve"
                    >
                        <Icon icon={FiCheck} className="h-4 w-4" />
                    </button>
                    {isRegional && (
                        <button
                            type="button"
                            onClick={() => handleForwardGeneral(row)}
                            className="rounded-lg p-1.5 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/20"
                            title="Forward to Head"
                        >
                            <Icon icon={FiSend} className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => triggerReject(row.id, 'general')}
                        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                        title="Reject"
                    >
                        <Icon icon={FiX} className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    const guardColumns: Column<Request>[] = [
        {
            header: 'Request ID',
            render: (row: Request) => <span className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200">{row.requestId || row.id.slice(-6).toUpperCase()}</span>
        },
        {
            header: 'Branch Code / Address',
            width: '28%',
            render: renderBranchColumn
        },
        {
            header: 'PSO',
            render: renderPsoColumn
        },
        {
            header: 'Guards Required',
            render: (row: Request) => <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{row.numberOfGuards || 0} Guards</span>
        },
        {
            header: 'Date Range',
            render: (row: Request) => (
                <div className="flex flex-col text-xs font-medium">
                    <span>From: {formatDateGlobal(row.guardDateFrom || row.cctvDateFrom)}</span>
                    <span className="text-slate-400 dark:text-slate-500">To: {formatDateGlobal(row.guardDateTo || row.cctvDateTo)}</span>
                </div>
            )
        },
        {
            header: 'No. of Days',
            render: (row: Request) => {
                const from = row.guardDateFrom || row.cctvDateFrom || '';
                const to = row.guardDateTo || row.cctvDateTo || '';
                if (!from || !to) return <span className="text-slate-400">-</span>;
                const fromUtc = Date.UTC(Number(from.slice(0,4)), Number(from.slice(5,7))-1, Number(from.slice(8,10)));
                const toUtc = Date.UTC(Number(to.slice(0,4)), Number(to.slice(5,7))-1, Number(to.slice(8,10)));
                const days = Math.floor((toUtc - fromUtc) / (1000 * 60 * 60 * 24)) + 1;
                return <span className="font-semibold text-slate-800 dark:text-slate-200">{days}</span>;
            }
        },
        {
            header: 'Total Cost',
            render: (row: Request) => <span className="font-bold text-blue-600 dark:text-blue-400">Rs. {row.totalAmount?.toLocaleString() || '—'}</span>
        },
        {
            header: 'Status',
            render: (row: Request) => (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700 dark:bg-amber-950/20">
                    PENDING
                </span>
            )
        },
        {
            header: 'Actions',
            align: 'right',
            width: '18%',
            render: (row: Request) => (
                <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={() => setSelectedRequest(row)}
                        className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => handleApproveGeneral(row)}
                        className="rounded-lg p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/20"
                        title="Approve"
                    >
                        <Icon icon={FiCheck} className="h-4 w-4" />
                    </button>
                    {isRegional && (
                        <button
                            type="button"
                            onClick={() => handleForwardGeneral(row)}
                            className="rounded-lg p-1.5 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/20"
                            title="Forward to Head"
                        >
                            <Icon icon={FiSend} className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => triggerReject(row.id, 'general')}
                        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                        title="Reject"
                    >
                        <Icon icon={FiX} className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    // Columns Definition: CCTV Hardware Requests
    const cctvHwColumns: Column<any>[] = [
        {
            header: 'PO / Request ID',
            render: (row: any) => (
                <div className="flex flex-col font-mono text-xs">
                    <span className="font-bold text-slate-800 dark:text-slate-200">{row.poNumber || '—'}</span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">{row.requestId || row.id.slice(-6).toUpperCase()}</span>
                </div>
            )
        },
        {
            header: 'Branch Code / Address',
            width: '28%',
            render: renderBranchColumn
        },
        {
            header: 'Requested Vendor',
            render: (row: any) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{row.vendor || '—'}</span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{row.requestCategory || '—'}</span>
                </div>
            )
        },
        {
            header: 'Items Count',
            render: (row: any) => <span className="font-medium text-slate-800 dark:text-slate-200 text-sm">{(row.items || []).length} Items</span>
        },
        {
            header: 'Status',
            render: (row: any) => {
                const status = row.status || 'pending';
                let bgClass = 'bg-amber-50 text-amber-700 dark:bg-amber-950/20';
                let label = 'PENDING';
                
                if (status === 'forwarded_to_regional') {
                    bgClass = 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/20';
                    label = 'FORWARDED TO REGIONAL';
                } else if (status === 'forwarded') {
                    bgClass = 'bg-blue-50 text-blue-700 dark:bg-blue-950/20';
                    label = 'FORWARDED';
                } else if (status === 'approved') {
                    bgClass = 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20';
                    label = 'APPROVED';
                } else if (status === 'rejected') {
                    bgClass = 'bg-rose-50 text-rose-700 dark:bg-rose-950/20';
                    label = 'REJECTED';
                }
                
                return (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${bgClass}`}>
                        {label}
                    </span>
                );
            }
        },
        {
            header: 'Actions',
            align: 'right',
            width: '18%',
            render: (row: any) => (
                <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={() => setSelectedHwRequest(row)}
                        className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {!isRegional && (
                        <button
                            type="button"
                            onClick={() => handleApproveHw(row)}
                            className="rounded-lg p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/20"
                            title="Approve"
                        >
                            <Icon icon={FiCheck} className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => triggerReject(row.id, 'hardware')}
                        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                        title="Reject"
                    >
                        <Icon icon={FiX} className="h-4 w-4" />
                    </button>
                    {isBankUser && (
                        <button
                            type="button"
                            onClick={() => handleDeleteHw(row)}
                            className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                            title="Delete"
                        >
                            <Icon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    // Columns Definition: BAMS Hardware Requests
    const bamsHwColumns: Column<any>[] = [
        {
            header: 'PO / Request ID',
            render: (row: any) => (
                <div className="flex flex-col font-mono text-xs">
                    <span className="font-bold text-slate-800 dark:text-slate-200">{row.poNumber || '—'}</span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">{row.requestId || row.id.slice(-6).toUpperCase()}</span>
                </div>
            )
        },
        {
            header: 'Branch Code / Address',
            width: '28%',
            render: renderBranchColumn
        },
        {
            header: 'Requested Vendor',
            render: (row: any) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{row.vendor || '—'}</span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{row.requestCategory || '—'}</span>
                </div>
            )
        },
        {
            header: 'Items Count',
            render: (row: any) => <span className="font-medium text-slate-800 dark:text-slate-200 text-sm">{(row.items || []).length} Items</span>
        },
        {
            header: 'Status',
            render: (row: any) => {
                const status = row.status || 'pending';
                let bgClass = 'bg-amber-50 text-amber-700 dark:bg-amber-950/20';
                let label = 'PENDING';
                
                if (status === 'forwarded_to_regional') {
                    bgClass = 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/20';
                    label = 'FORWARDED TO REGIONAL';
                } else if (status === 'forwarded') {
                    bgClass = 'bg-blue-50 text-blue-700 dark:bg-blue-950/20';
                    label = 'FORWARDED';
                } else if (status === 'approved') {
                    bgClass = 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20';
                    label = 'APPROVED';
                } else if (status === 'rejected') {
                    bgClass = 'bg-rose-50 text-rose-700 dark:bg-rose-950/20';
                    label = 'REJECTED';
                }
                
                return (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${bgClass}`}>
                        {label}
                    </span>
                );
            }
        },
        {
            header: 'Actions',
            align: 'right',
            width: '18%',
            render: (row: any) => (
                <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={() => setSelectedHwRequest(row)}
                        className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {!isRegional && (
                        <button
                            type="button"
                            onClick={() => handleApproveHw(row)}
                            className="rounded-lg p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/20"
                            title="Approve"
                        >
                            <Icon icon={FiCheck} className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => triggerReject(row.id, 'hardware')}
                        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                        title="Reject"
                    >
                        <Icon icon={FiX} className="h-4 w-4" />
                    </button>
                    {isBankUser && (
                        <button
                            type="button"
                            onClick={() => handleDeleteHw(row)}
                            className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                            title="Delete"
                        >
                            <Icon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    // Columns Definition: Travelling Approvals
    const travelColumns: Column<TravellingApprovalRequest>[] = [
        {
            header: 'Request ID',
            render: (row: TravellingApprovalRequest) => <span className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200">{row.requestId || row.id?.slice(-6).toUpperCase()}</span>
        },
        {
            header: 'PSO / Sender',
            render: (row: TravellingApprovalRequest) => {
                const forwardedByName = (row as any).forwardedBy ? psoNameByCreatorId[(row as any).forwardedBy] : '';
                return (
                    <div className="flex flex-col">
                        <span className="font-bold text-slate-900 dark:text-white">{row.psoName || '—'}</span>
                        <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">{row.psoEmail || '—'}</span>
                        {isHead && forwardedByName && (
                            <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">
                                Forwarded by: {forwardedByName}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Visits / Branches',
            width: '28%',
            render: (row: TravellingApprovalRequest) => (
                <div className="flex flex-col gap-0.5 text-xs">
                    {(row.branchNames || []).slice(0, 2).map((name: string, idx: number) => (
                        <div key={idx} className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-xs">{name}</div>
                    ))}
                    {(row.branchNames || []).length > 2 && (
                        <div className="text-[10px] text-slate-400 italic">+{(row.branchNames || []).length - 2} more branches</div>
                    )}
                </div>
            )
        },
        {
            header: 'Visit Date',
            render: (row: TravellingApprovalRequest) => (
                <div className="flex flex-col text-xs font-medium">
                    <span>{formatDateGlobal(row.dateOfVisit)}</span>
                    {row.lastDateOfVisit && <span className="text-slate-400 dark:text-slate-500">to {formatDateGlobal(row.lastDateOfVisit)}</span>}
                </div>
            )
        },
        {
            header: 'Claim Amount',
            render: (row: TravellingApprovalRequest) => (
                <div className="flex flex-col text-xs font-semibold text-slate-900 dark:text-white">
                    <span>Rs. {row.amountClaim?.toLocaleString() || 0}</span>
                </div>
            )
        },
        {
            header: 'Status',
            render: (row: TravellingApprovalRequest) => (
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                    row.status === 'forwarded' ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/20' :
                    'bg-amber-50 text-amber-700 dark:bg-amber-950/20'
                }`}>
                    {row.status?.toUpperCase() || 'PENDING'}
                </span>
            )
        },
        {
            header: 'Actions',
            align: 'right',
            width: '18%',
            render: (row: TravellingApprovalRequest) => (
                <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={() => setSelectedTravelRequest(row)}
                        className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => triggerTravelApprove(row.id || '', row.amountClaim || 0)}
                        className="rounded-lg p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/20"
                        title="Approve & Set Amount"
                    >
                        <Icon icon={FiCheck} className="h-4 w-4" />
                    </button>
                    {isRegional && row.status === 'pending' && (
                        <button
                            type="button"
                            onClick={() => handleForwardTravel(row.id || '')}
                            className="rounded-lg p-1.5 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/20"
                            title="Forward to Head"
                        >
                            <Icon icon={FiSend} className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => triggerReject(row.id || '', 'travel')}
                        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                        title="Reject"
                    >
                        <Icon icon={FiX} className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];



    return (
        <div className="space-y-6 p-1">
            {toast.show && (
                <div className="fixed right-4 top-4 z-[9999]">
                    <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
                </div>
            )}

            {/* Dashboard Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                        Request Summary
                    </h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Real-time unified workspace for managing pending branch requests, travel claims, and Change of Vendors.
                    </p>
                </div>
            </div>

            {/* Stat Cards (8 items grid) */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8">
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-blue-50 p-2 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400">
                            <Icon icon={FiVideo} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">CCTV Gen</p>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{counts.cctv}</h3>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400">
                            <Icon icon={FiRadio} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">BAMS Gen</p>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{counts.bams}</h3>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-blue-100 p-2 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400">
                            <Icon icon={FiCpu} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">CCTV HW</p>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{counts.cctvHw}</h3>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-indigo-100 p-2 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-400">
                            <Icon icon={FiCpu} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">BAMS HW</p>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{counts.bamsHw}</h3>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-purple-50 p-2 text-purple-600 dark:bg-purple-950/30 dark:text-purple-400">
                            <Icon icon={FiShield} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Guard</p>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{counts.guard}</h3>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-orange-50 p-2 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400">
                            <Icon icon={FiFileText} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Travel</p>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{counts.travel}</h3>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-emerald-50 p-2 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
                            <Icon icon={FiRefreshCw} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">COV</p>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{counts.cov}</h3>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 shadow-sm transition hover:shadow-md dark:border-amber-950/20 dark:bg-amber-950/10">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-amber-100 p-2 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                            <Icon icon={FiClock} className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Total</p>
                            <h3 className="text-xl font-extrabold text-amber-900 dark:text-amber-300">{counts.pending}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs & Filters */}
            <div className="flex flex-col gap-4 border-b border-slate-200 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-2 overflow-x-auto pb-px">
                    <button
                        onClick={() => setActiveTab('cctv')}
                        className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap ${
                            activeTab === 'cctv'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                        }`}
                    >
                        <Icon icon={FiVideo} className="h-4 w-4" />
                        CCTV General ({cctvRequests.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('bams')}
                        className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap ${
                            activeTab === 'bams'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                        }`}
                    >
                        <Icon icon={FiRadio} className="h-4 w-4" />
                        BAMS General ({bamsRequests.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('guard')}
                        className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap ${
                            activeTab === 'guard'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                        }`}
                    >
                        <Icon icon={FiShield} className="h-4 w-4" />
                        Guard ({guardRequests.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('cctv_hw')}
                        className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap ${
                            activeTab === 'cctv_hw'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                        }`}
                    >
                        <Icon icon={FiCpu} className="h-4 w-4" />
                        CCTV Hardware ({cctvHardwareRequests.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('bams_hw')}
                        className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap ${
                            activeTab === 'bams_hw'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                        }`}
                    >
                        <Icon icon={FiCpu} className="h-4 w-4" />
                        BAMS Hardware ({bamsHardwareRequests.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('travel')}
                        className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap ${
                            activeTab === 'travel'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                        }`}
                    >
                        <Icon icon={FiFileText} className="h-4 w-4" />
                        Travelling Approvals ({filteredTravelRequests.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('cov')}
                        className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap ${
                            activeTab === 'cov'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                        }`}
                    >
                        <Icon icon={FiRefreshCw} className="h-4 w-4" />
                        Change of Vendor ({filteredChangeOfVendorRequests.length})
                    </button>
                </div>

               
            </div>

            {/* Table workspace */}
            <div>
                {activeTab === 'cctv' && (
                    <Table
                        data={cctvRequests}
                        columns={cctvColumns}
                        keyExtractor={(item: Request) => item.id}
                        loading={loading}
                        emptyMessage="No pending CCTV requests found."
                        onRowClick={(item: Request) => setSelectedRequest(item)}
                    />
                )}

                {activeTab === 'bams' && (
                    <Table
                        data={bamsRequests}
                        columns={bamsColumns}
                        keyExtractor={(item: Request) => item.id}
                        loading={loading}
                        emptyMessage="No pending BAMS requests found."
                        onRowClick={(item: Request) => setSelectedRequest(item)}
                    />
                )}

                {activeTab === 'guard' && (
                    <Table
                        data={guardRequests}
                        columns={guardColumns}
                        keyExtractor={(item: Request) => item.id}
                        loading={loading}
                        emptyMessage="No pending Guard requests found."
                        onRowClick={(item: Request) => setSelectedRequest(item)}
                    />
                )}

                {activeTab === 'cctv_hw' && (
                    <CCTVPsoRequest
                        companyId={bankId}
                        branches={branches}
                        vendors={companies}
                        deployments={[]}
                        uid={user?.uid}
                        pso={pso}
                        onInitiatePO={() => {}}
                        summaryMode={true}
                    />
                )}

                {activeTab === 'bams_hw' && (
                    <BAMSPsoRequest
                        companyId={bankId}
                        branches={branches}
                        vendors={companies}
                        deployments={[]}
                        uid={user?.uid}
                        pso={pso}
                        onInitiatePO={() => {}}
                        summaryMode={true}
                    />
                )}

                {activeTab === 'travel' && (
                    <Table
                        data={filteredTravelRequests}
                        columns={travelColumns}
                        keyExtractor={(item: TravellingApprovalRequest) => item.id || ''}
                        loading={loading}
                        emptyMessage="No pending Travelling Approvals found."
                        onRowClick={(item: TravellingApprovalRequest) => setSelectedTravelRequest(item)}
                    />
                )}

                {activeTab === 'cov' && (
                    <ChangeOfVendors summaryMode={true} />
                )}
            </div>

            {/* CCTV / BAMS / Guard details modal */}
            {selectedRequest && (
                <Modal
                    isOpen={!!selectedRequest}
                    onClose={() => setSelectedRequest(null)}
                    title={`Request Details: ${selectedRequest.requestId || selectedRequest.id}`}
                    size="lg"
                >
                    <div className="space-y-6 p-6">
                        <div className="grid grid-cols-2 gap-6 border-b border-slate-100 pb-6 dark:border-slate-800">
                            <div>
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">Request Type</span>
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-lg">{selectedRequest.requestType}</span>
                            </div>
                            <div>
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">Status</span>
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold mt-1 text-amber-700 dark:bg-amber-950/20">
                                    PENDING
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-xs font-bold text-slate-400 uppercase block">Branch Code</span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedRequest.branchCode || '—'}</span>
                            </div>
                            <div>
                                <span className="text-xs font-bold text-slate-400 uppercase block">Branch Name / Address</span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedRequest.branchName || '—'}</span>
                            </div>
                            {(() => {
                                const branch = branches.find(b => b.id === selectedRequest.branchId || b.branchCode === selectedRequest.branchCode);
                                const locationName = branch?.locationName || (branch?.locationId ? locationNameMap[branch.locationId] : '') || '';
                                return locationName ? (
                                    <div>
                                        <span className="text-xs font-bold text-slate-400 uppercase block">Region / Location</span>
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{locationName}</span>
                                    </div>
                                ) : null;
                            })()}
                            {(() => {
                                const id = selectedRequest.createdBy || '';
                                const psoName = id ? psoNameByCreatorId[id] : '';
                                return psoName ? (
                                    <div>
                                        <span className="text-xs font-bold text-slate-400 uppercase block">PSO Name</span>
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{psoName}</span>
                                    </div>
                                ) : null;
                            })()}
                            {isHead && selectedRequest.forwardedBy && (() => {
                                const forwardedByName = psoNameByCreatorId[selectedRequest.forwardedBy];
                                return forwardedByName ? (
                                    <div>
                                        <span className="text-xs font-bold text-slate-400 uppercase block">Forwarded By</span>
                                        <span className="text-sm font-semibold text-slate-850 dark:text-slate-100">{forwardedByName}</span>
                                    </div>
                                ) : null;
                            })()}
                            {selectedRequest.requesterName && selectedRequest.requesterName !== 'N/A' && selectedRequest.requesterName !== '—' && (
                                <div>
                                    <span className="text-xs font-bold text-slate-400 uppercase block">Requester Name</span>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedRequest.requesterName}</span>
                                </div>
                            )}
                            {selectedRequest.requesterContact && selectedRequest.requesterContact !== 'N/A' && selectedRequest.requesterContact !== '—' && (
                                <div>
                                    <span className="text-xs font-bold text-slate-400 uppercase block">Requester Contact</span>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedRequest.requesterContact}</span>
                                </div>
                            )}
                            {selectedRequest.requestType === 'CCTV' && selectedRequest.cctvDateRanges && selectedRequest.cctvDateRanges.length > 0 ? (
                                <div className="col-span-2">
                                    <span className="text-xs font-bold text-slate-400 uppercase block mb-1">Required Date Ranges</span>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        {selectedRequest.cctvDateRanges.map((range: any, idx: number) => (
                                            <div key={idx} className="flex items-center justify-between rounded-xl border border-gray-150 bg-slate-50/50 dark:bg-slate-800/40 p-2 text-xs dark:border-gray-700">
                                                <div className="flex flex-col gap-0.5">
                                                    <div className="flex items-center gap-1.5">
                                                        <Icon icon={FiCalendar} className="text-blue-500" />
                                                        <span className="font-semibold text-slate-800 dark:text-slate-100">
                                                            {formatDateGlobal(range.dateFrom)} &rarr; {formatDateGlobal(range.dateTo)}
                                                        </span>
                                                    </div>
                                                    {range.timeFrom && range.timeTo && (
                                                        <span className="text-slate-500 dark:text-slate-400 ml-5 font-medium">
                                                            Time: {range.timeFrom} - {range.timeTo}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="rounded bg-blue-50 px-1.5 py-0.5 font-bold text-[10px] text-blue-700 dark:bg-blue-950/20 dark:text-blue-400">
                                                    {dayjs(range.dateTo).diff(dayjs(range.dateFrom), 'day') + 1} days
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <span className="text-xs font-bold text-slate-400 uppercase block">Required Date range</span>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                        {formatDateGlobal(selectedRequest.cctvDateFrom || selectedRequest.bamsDateFrom || selectedRequest.guardDateFrom)} to {formatDateGlobal(selectedRequest.cctvDateTo || selectedRequest.bamsDateTo || selectedRequest.guardDateTo)}
                                    </span>
                                </div>
                            )}
                            {selectedRequest.requestType === 'CCTV' && (
                                <div>
                                    <span className="text-xs font-bold text-slate-400 uppercase block">Footage Time Range</span>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedRequest.timeFrom || '—'} to {selectedRequest.timeTo || '—'}</span>
                                </div>
                            )}
                            {selectedRequest.requestType === 'BAMS' && (
                                <div>
                                    <span className="text-xs font-bold text-slate-400 uppercase block">Hardware Required</span>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedRequest.hardwareName || '—'}</span>
                                </div>
                            )}
                            {selectedRequest.requestType === 'Guard' && (
                                <>
                                    <div>
                                        <span className="text-xs font-bold text-slate-400 uppercase block">Number of Guards</span>
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedRequest.numberOfGuards || 0}</span>
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold text-slate-400 uppercase block">Daily Rate</span>
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Rs. {selectedRequest.guardDailyRate?.toLocaleString() || 0}</span>
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold text-slate-400 uppercase block">Total Amount</span>
                                        <span className="text-sm font-bold text-blue-600 dark:text-blue-400">Rs. {selectedRequest.totalAmount?.toLocaleString() || 0}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
                            <span className="text-xs font-bold text-slate-400 uppercase block">Purpose of Request</span>
                            <p className="text-sm font-semibold text-slate-850 mt-1 leading-relaxed dark:text-slate-200">
                                {getFormattedPurpose(selectedRequest)}
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 border-t border-slate-100 pt-6 dark:border-slate-800">
                            <Button variant="secondary" onClick={() => setSelectedRequest(null)}>
                                Close
                            </Button>
                            <Button variant="danger" onClick={() => {
                                triggerReject(selectedRequest.id, 'general');
                                setSelectedRequest(null);
                            }}>
                                Reject
                            </Button>
                            {isRegional && (
                                <Button variant="primary" onClick={() => {
                                    handleForwardGeneral(selectedRequest);
                                    setSelectedRequest(null);
                                }}>
                                    Forward to Head
                                </Button>
                            )}
                            <Button variant="primary" onClick={() => {
                                handleApproveGeneral(selectedRequest);
                                setSelectedRequest(null);
                            }}>
                                Approve
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}



            {/* Travelling approval details modal */}
            {selectedTravelRequest && (
                <Modal
                    isOpen={!!selectedTravelRequest}
                    onClose={() => setSelectedTravelRequest(null)}
                    title={`Travelling Approval Details: ${selectedTravelRequest.requestId || selectedTravelRequest.id}`}
                    size="lg"
                >
                    <div className="space-y-6 p-6">
                        <div className="grid grid-cols-2 gap-6 border-b border-slate-100 pb-6 dark:border-slate-800">
                            <div>
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">Sender / PSO</span>
                                <span className="font-bold text-slate-850 dark:text-slate-100 text-lg">{selectedTravelRequest.psoName}</span>
                            </div>
                            <div>
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">Status</span>
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold mt-1 text-amber-700 dark:bg-amber-950/20">
                                    {selectedTravelRequest.status?.toUpperCase() || 'PENDING'}
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-xs font-bold text-slate-400 uppercase block">Visit Dates</span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                    {formatDateGlobal(selectedTravelRequest.dateOfVisit)} to {formatDateGlobal(selectedTravelRequest.lastDateOfVisit)}
                                </span>
                            </div>
                            <div>
                                <span className="text-xs font-bold text-slate-400 uppercase block">Estimated Distance</span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedTravelRequest.distance} km</span>
                            </div>
                            <div>
                                <span className="text-xs font-bold text-slate-400 uppercase block">Night Stay Required</span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedTravelRequest.nightStay ? 'Yes' : 'No'}</span>
                            </div>
                            <div>
                                <span className="text-xs font-bold text-slate-400 uppercase block">Modality of Travel</span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 capitalize">{selectedTravelRequest.travelModality || '—'}</span>
                            </div>
                            <div>
                                <span className="text-xs font-bold text-slate-400 uppercase block">Claimed Amount</span>
                                <span className="text-sm font-bold text-slate-850 dark:text-slate-100">Rs. {selectedTravelRequest.amountClaim?.toLocaleString()}</span>
                            </div>
                            {isHead && (selectedTravelRequest as any).forwardedBy && (() => {
                                const forwardedByName = psoNameByCreatorId[(selectedTravelRequest as any).forwardedBy];
                                return forwardedByName ? (
                                    <div>
                                        <span className="text-xs font-bold text-slate-400 uppercase block">Forwarded By</span>
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{forwardedByName}</span>
                                    </div>
                                ) : null;
                            })()}
                        </div>

                        <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
                            <span className="text-xs font-bold text-slate-400 uppercase block">Branches Visited</span>
                            <ul className="list-disc list-inside mt-2 text-sm text-slate-700 dark:text-slate-350 space-y-1 font-medium">
                                {(selectedTravelRequest.branchNames || []).map((name: string, idx: number) => (
                                    <li key={idx}>{name}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
                            <span className="text-xs font-bold text-slate-400 uppercase block">Purpose of Visit</span>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">
                                {selectedTravelRequest.purpose === 'other' ? selectedTravelRequest.purposeOtherDetail : selectedTravelRequest.purpose || '—'}
                            </p>
                        </div>

                        {selectedTravelRequest.remarks && (
                            <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
                                <span className="text-xs font-bold text-slate-400 uppercase block">PSO Remarks</span>
                                <p className="text-sm font-medium text-slate-650 dark:text-slate-400 mt-1 italic font-semibold">
                                    "{selectedTravelRequest.remarks}"
                                </p>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 border-t border-slate-100 pt-6 dark:border-slate-800">
                            <Button variant="secondary" onClick={() => setSelectedTravelRequest(null)}>
                                Close
                            </Button>
                            <Button variant="danger" onClick={() => {
                                triggerReject(selectedTravelRequest.id || '', 'travel');
                                setSelectedTravelRequest(null);
                            }}>
                                Reject
                            </Button>
                            {isRegional && selectedTravelRequest.status === 'pending' && (
                                <Button variant="primary" onClick={() => {
                                    handleForwardTravel(selectedTravelRequest.id || '');
                                    setSelectedTravelRequest(null);
                                }}>
                                    Forward to Head
                                </Button>
                            )}
                            <Button variant="primary" onClick={() => {
                                triggerTravelApprove(selectedTravelRequest.id || '', selectedTravelRequest.amountClaim || 0);
                                setSelectedTravelRequest(null);
                            }}>
                                Approve
                            </Button>
                        </div>
                                  </div>
                </Modal>
            )}

            {/* Rejection reason modal */}
            {showRejectModal && (
                <Modal
                    isOpen={showRejectModal}
                    onClose={() => setShowRejectModal(false)}
                    title="Provide Rejection Reason"
                    size="md"
                >
                    <div className="space-y-4 p-6">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                                Reason for Rejection <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                className="w-full min-h-[120px] rounded-xl border border-slate-200 bg-white py-3 px-4 text-sm font-medium text-slate-900 outline-none transition shadow-sm hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-slate-800 dark:text-white dark:border-slate-750"
                                placeholder="State the reason why this request is being rejected..."
                                required
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleConfirmReject}
                                disabled={!rejectReason.trim()}
                            >
                                Confirm Rejection
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Travelling approval confirm amount modal */}
            {showTravelApproveModal && (
                <Modal
                    isOpen={showTravelApproveModal}
                    onClose={() => setShowTravelApproveModal(false)}
                    title="Confirm Approved Travel Claim"
                    size="md"
                >
                    <div className="space-y-4 p-6">
                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                                Confirm Approved Amount (Rs.) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                value={confirmAmount}
                                onChange={(e) => setConfirmAmount(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-blue-500 dark:bg-slate-800 dark:text-white dark:border-slate-750"
                                required
                            />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                                Approver Remarks
                            </label>
                            <textarea
                                value={approverRemarks}
                                onChange={(e) => setApproverRemarks(e.target.value)}
                                className="w-full min-h-[80px] rounded-xl border border-slate-200 bg-white py-2 px-4 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500 dark:bg-slate-800 dark:text-white dark:border-slate-750"
                                placeholder="Optional remarks on this approval..."
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="secondary" onClick={() => setShowTravelApproveModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleConfirmTravelApprove}
                                disabled={!confirmAmount.trim()}
                            >
                                Approve Claim
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}
